import staticRoutes from "./routes.seo.json";

/**
 * Route-level SEO rules.
 *
 * `routes.seo.json` holds every *static* route with its metadata and its
 * explicit indexability flag. It is plain JSON on purpose: the prerender and
 * sitemap scripts (plain Node) read the exact same file the React app reads,
 * so the public/private classification can never drift between the two.
 *
 * Dynamic routes get their title/description from fetched data at render time,
 * so only their indexability pattern lives here.
 */

/** Dynamic public route patterns. Order matters only for readability. */
const DYNAMIC_INDEXABLE_PATTERNS = [
  /^\/menuDetails\/[^/]+$/,
  /^\/menubyCategory\/[^/]+$/,
  /^\/seo-pages\/[^/]+$/,
  /^\/seo-category\/[^/]+$/,
];

/** Strips query/hash and the trailing slash so lookups are stable. */
export function normalisePath(pathname = "/") {
  const [withoutHash] = String(pathname).split("#");
  const [clean] = withoutHash.split("?");
  const withLeading = clean.startsWith("/") ? clean : `/${clean}`;
  const trimmed = withLeading.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

const byPath = new Map(staticRoutes.map((route) => [route.path, route]));

/** Returns the static SEO entry for a path, or undefined for dynamic/unknown routes. */
export function getRouteSeo(pathname) {
  return byPath.get(normalisePath(pathname));
}

/**
 * Whether a path may be indexed.
 *
 * Unknown paths default to NOT indexable: an unrecognised URL is either the
 * 404 page or a route someone added without classifying it, and in both cases
 * "noindex" is the safe default.
 */
export function isIndexable(pathname) {
  const path = normalisePath(pathname);

  const entry = byPath.get(path);
  if (entry) return Boolean(entry.index);

  return DYNAMIC_INDEXABLE_PATTERNS.some((pattern) => pattern.test(path));
}

/** Static routes that should be indexed — used by the sitemap generator. */
export const indexableStaticRoutes = staticRoutes.filter((r) => r.index);

/** Static routes that should get a prerendered HTML file. */
export const prerenderStaticRoutes = staticRoutes.filter((r) => r.prerender);

export default { getRouteSeo, isIndexable, normalisePath };
